/**
 * Human-readable, monotonic, prefixed document numbers (SCOPE §18.1-3).
 *
 * Staff read these aloud over the phone and type them into search. The prefix
 * makes the kind obvious ("PO-1042" is unmistakably a purchase order), and the
 * starting values give every number a stable digit count for years.
 *
 * Sequences are per-tenant and allocated by the database, not here — this
 * module only owns the *shape*.
 */

export type DocumentKind =
  | 'sale'
  | 'purchaseOrder'
  | 'receivingOrder'
  | 'repairTicket'
  | 'vendorInvoice'
  | 'consignment'
  | 'countSession'
  | 'drawerSession'
  | 'feedback'
  /** An order placed on the web shop (docs/STOREFRONT.md S4). */
  | 'webOrder'
  /** Goods going back to a vendor (decision 181). */
  | 'vendorReturn';

export const DOCUMENT_PREFIXES: Readonly<Record<DocumentKind, string>> = {
  sale: 'S',
  purchaseOrder: 'PO',
  receivingOrder: 'RO',
  repairTicket: 'RT',
  vendorInvoice: 'VI',
  /** Also the barcode on the item's label, so it scans and reads aloud the same. */
  consignment: 'CN',
  countSession: 'CT',
  drawerSession: 'DS',
  /** Quotable back to the person who reported it: "FB-1042 is fixed." */
  feedback: 'FB',
  /** What the customer is emailed and quotes: "my order W-1042". */
  webOrder: 'W',
  /** The shop's own shorthand: "write an RTV". */
  vendorReturn: 'RTV',
};

/** First number each sequence issues. Chosen so numbers never change width in practice. */
export const DOCUMENT_SEQUENCE_START: Readonly<Record<DocumentKind, number>> = {
  sale: 100001,
  purchaseOrder: 1001,
  receivingOrder: 1001,
  repairTicket: 1001,
  vendorInvoice: 1001,
  consignment: 1001,
  countSession: 1001,
  drawerSession: 1001,
  feedback: 1001,
  webOrder: 1001,
  vendorReturn: 1001,
};

const KIND_BY_PREFIX = new Map<string, DocumentKind>(
  (Object.entries(DOCUMENT_PREFIXES) as Array<[DocumentKind, string]>).map(([kind, prefix]) => [
    prefix,
    kind,
  ]),
);

export function formatDocumentNumber(kind: DocumentKind, sequence: number): string {
  if (!Number.isInteger(sequence) || sequence < 0) {
    throw new RangeError(`formatDocumentNumber: sequence must be a non-negative integer`);
  }
  return `${DOCUMENT_PREFIXES[kind]}-${sequence}`;
}

export interface ParsedDocumentNumber {
  readonly kind: DocumentKind;
  readonly sequence: number;
}

/**
 * Parse `"PO-1042"` back into its parts. Case-insensitive and tolerant of a
 * missing hyphen, because staff type `po1042` into the search box.
 */
export function parseDocumentNumber(value: string): ParsedDocumentNumber | null {
  const match = /^([A-Za-z]+)-?(\d+)$/.exec(value.trim());
  if (match === null) return null;

  const kind = KIND_BY_PREFIX.get(match[1]!.toUpperCase());
  if (kind === undefined) return null;

  const sequence = Number(match[2]);
  if (!Number.isSafeInteger(sequence)) return null;

  return { kind, sequence };
}

/**
 * What a purchase order's reference is, as it is printed and emailed.
 *
 * The reference is the vendor's own number for the order — except on an order
 * brought over from Rain, where the importer keeps the store's old Rain PO
 * number there (`Rain PO 3204012762`; decision 206 numbers it R-2762 from its
 * last four). Printed as "Their order number" on paper going to the vendor,
 * that named the store's number as theirs (FB-1130, 2026-10-21). It is the
 * number the vendor first had the order under, so it stays on the paper —
 * named for what it is.
 */
export function purchaseOrderReference(
  reference: string | null | undefined,
): { readonly label: string; readonly emailLabel: string; readonly value: string } | null {
  if (reference == null || reference.trim() === '') return null;
  const rain = /^Rain PO (.+)$/.exec(reference.trim());
  if (rain !== null) {
    return {
      label: 'First sent as',
      emailLabel: 'Our earlier PO number',
      value: `PO ${rain[1]!} (Rain)`,
    };
  }
  return { label: 'Their order number', emailLabel: 'Your reference', value: reference.trim() };
}
